package com.trevorwiebe.timesheet.admin.reporting.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.admin.reporting.domain.MissingStartEndBoundary
import com.trevorwiebe.timesheet.admin.customer.domain.CustomerRepository
import com.trevorwiebe.timesheet.admin.reporting.domain.buildMissingStartEndFix
import com.trevorwiebe.timesheet.admin.reporting.domain.calculateGeneralMileageReport
import com.trevorwiebe.timesheet.admin.reporting.domain.calculateIftaReport
import com.trevorwiebe.timesheet.admin.reporting.domain.calculateMissingStartEndReport
import com.trevorwiebe.timesheet.admin.reporting.domain.calculateRevenuePerMileReport
import com.trevorwiebe.timesheet.admin.reporting.domain.findLoadsWithoutTruck
import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.Customer
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.loads.domain.LoadsRepository
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.model.hasCarrier
import com.trevorwiebe.timesheet.loads.domain.model.isCompleted
import com.trevorwiebe.timesheet.loads.domain.resolveTruck
import com.trevorwiebe.timesheet.loads.domain.resolvedTruckId
import com.trevorwiebe.timesheet.loads.presentation.admin.todayDate
import com.trevorwiebe.timesheet.mileage.domain.EquipmentRepository
import com.trevorwiebe.timesheet.mileage.domain.LocationRepository
import com.trevorwiebe.timesheet.mileage.domain.MileageRepository
import com.trevorwiebe.timesheet.mileage.domain.model.Equipment
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.mileage.domain.model.OdometerNeighbors
import com.trevorwiebe.timesheet.mileage.domain.model.OdometerPlacement
import com.trevorwiebe.timesheet.mileage.domain.model.TripTypeData
import com.trevorwiebe.timesheet.mileage.domain.model.evaluateOdometerPlacement
import com.trevorwiebe.timesheet.mileage.domain.model.odometerConflictMessage
import com.trevorwiebe.timesheet.mileage.domain.model.odometerLookupErrorMessage
import com.trevorwiebe.timesheet.mileage.domain.usecases.AddMileageEntry
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.plus

class ReportingViewModel(
    private val mileageRepository: MileageRepository,
    private val equipmentRepository: EquipmentRepository,
    private val coreRepository: CoreRepository,
    private val addMileageEntry: AddMileageEntry,
    private val loadsRepository: LoadsRepository,
    private val customerRepository: CustomerRepository,
    private val locationRepository: LocationRepository,
) : DisposableViewModel() {

    private val _state = MutableStateFlow(ReportingState())
    val state = _state.asStateFlow()

    private var entriesJob: Job? = null
    private var matchEntriesJob: Job? = null
    private var loadDataStarted = false

    init {
        val (quarterStart, today) = Util.currentQuarterRange()
        _state.update { it.copy(filter = it.filter.copy(startDate = quarterStart, endDate = today)) }

        loadEntries()
        loadEquipment()
        loadUserList()
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadEntries() {
        val filter = _state.value.filter
        val start = filter.startDate ?: return
        val end = filter.endDate ?: return

        entriesJob?.cancel()
        _state.update { it.copy(isLoading = true) }
        val job = viewModelScope.launch {
            val flow = mileageRepository.queryMileageEntries(
                startDate = start,
                endDate = end,
                employeeId = filter.employeeId,
                equipmentId = filter.equipmentId,
                tripTypes = filter.tripTypes,
            )
            flow.collect { result ->
                if (result.error.isNullOrEmpty()) {
                    val entries = result.data as List<MileageEntry>
                    _state.update {
                        it.copy(
                            entries = entries,
                            iftaRows = calculateIftaReport(entries),
                            generalMileageRows = calculateGeneralMileageReport(entries, it.equipment),
                            missingStartEndRows = calculateMissingStartEndReport(entries, it.equipment),
                            isLoading = false,
                        ).withLoadReports()
                    }
                } else {
                    _state.update { it.copy(isLoading = false, error = result.error) }
                }
            }
        }
        entriesJob = job
        FirestoreListenerRegistry.registerJob(job)
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadEquipment() {
        val job = viewModelScope.launch {
            equipmentRepository.getEquipment().collect { result ->
                if (result.error.isNullOrEmpty()) {
                    val equipment = result.data as List<Equipment>
                    _state.update {
                        it.copy(
                            equipment = equipment,
                            generalMileageRows = calculateGeneralMileageReport(it.entries, equipment),
                            missingStartEndRows = calculateMissingStartEndReport(it.entries, equipment),
                        ).withLoadReports()
                    }
                }
            }
        }
        FirestoreListenerRegistry.registerJob(job)
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadUserList() {
        viewModelScope.launch {
            val result = coreRepository.getUserList()
            if (result.error.isNullOrEmpty()) {
                _state.update { it.copy(userList = result.data as List<DatabaseUserModel>).withLoadReports() }
            }
        }
    }

    /** Subscribes to the load-report inputs the first time a load-based report is opened. */
    @Suppress("UNCHECKED_CAST")
    private fun ensureLoadReportData() {
        if (loadDataStarted) return
        loadDataStarted = true
        loadMatchEntries()
        FirestoreListenerRegistry.registerJob(viewModelScope.launch {
            loadsRepository.queryLoads().collect { result ->
                if (result.error.isNullOrEmpty()) {
                    _state.update { it.copy(loads = result.data as List<Load>, loadsLoaded = true).withLoadReports() }
                } else {
                    _state.update { it.copy(error = result.error, loadsLoaded = true) }
                }
            }
        })
        FirestoreListenerRegistry.registerJob(viewModelScope.launch {
            customerRepository.getCustomers().collect { result ->
                val customers = result.data as? List<Customer> ?: return@collect
                _state.update { it.copy(customers = customers).withLoadReports() }
            }
        })
        FirestoreListenerRegistry.registerJob(viewModelScope.launch {
            locationRepository.getLocations().collect { result ->
                val locations = result.data as? List<Location> ?: return@collect
                _state.update { it.copy(locations = locations).withLoadReports() }
            }
        })
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadMatchEntries() {
        if (!loadDataStarted) return
        val filter = _state.value.filter
        val start = filter.startDate ?: return
        val end = filter.endDate ?: return

        matchEntriesJob?.cancel()
        _state.update { it.copy(matchEntriesLoaded = false) }
        val job = viewModelScope.launch {
            mileageRepository.queryMileageEntries(
                startDate = start,
                endDate = end.plus(1, DateTimeUnit.DAY),
            ).collect { result ->
                if (result.error.isNullOrEmpty()) {
                    _state.update {
                        it.copy(matchEntries = result.data as List<MileageEntry>, matchEntriesLoaded = true)
                            .withLoadReports()
                    }
                } else {
                    _state.update { it.copy(error = result.error, matchEntriesLoaded = true) }
                }
            }
        }
        matchEntriesJob = job
        FirestoreListenerRegistry.registerJob(job)
    }

    private fun ReportingState.withLoadReports(): ReportingState {
        val start = filter.startDate ?: return this
        val end = filter.endDate ?: return this
        val today = todayDate()
        val loadsInRange = loads.filter { load ->
            val date = load.scheduledDate
            date != null && date in start..end && load.isCompleted(today) && !load.hasCarrier &&
                (filter.employeeId == null || load.employeeId == filter.employeeId)
        }
        val matches = loadsInRange.associate { it.id to resolveTruck(it, matchEntries) }
        val revenueLoads = filter.equipmentId?.let { truckId ->
            loadsInRange.filter { matches[it.id]?.resolvedTruckId == truckId }
        } ?: loadsInRange
        return copy(
            revenuePerMile = calculateRevenuePerMileReport(revenueLoads, matches, generalMileageRows, equipment),
            loadsWithoutTruckRows = findLoadsWithoutTruck(
                loadsInRange, matches, customers, userList, locations, equipment,
            ),
        )
    }

    private fun setTruck(equipmentId: String) {
        val dialog = _state.value.setTruckDialog ?: return
        _state.update { it.copy(setTruckDialog = dialog.copy(isSaving = true, error = null)) }
        viewModelScope.launch {
            val result = loadsRepository.updateLoad(dialog.row.load.copy(truckId = equipmentId))
            _state.update {
                if (result.error.isNullOrEmpty()) it.copy(setTruckDialog = null)
                else it.copy(setTruckDialog = dialog.copy(isSaving = false, error = result.error))
            }
        }
    }

    fun onEvent(event: ReportingEvent) {
        when (event) {
            is ReportingEvent.OnFilterByEquipment -> {
                _state.update { it.copy(filter = it.filter.copy(equipmentId = event.equipmentId)) }
                loadEntries()
            }

            is ReportingEvent.OnFilterByEmployee -> {
                _state.update { it.copy(filter = it.filter.copy(employeeId = event.employeeId)) }
                loadEntries()
            }

            ReportingEvent.OnStartDateClick ->
                _state.update { it.copy(filter = it.filter.copy(showStartPicker = true)) }

            ReportingEvent.OnEndDateClick ->
                _state.update { it.copy(filter = it.filter.copy(showEndPicker = true)) }

            is ReportingEvent.OnStartDateSelected -> {
                _state.update { it.copy(filter = it.filter.copy(startDate = event.date, showStartPicker = false)) }
                loadEntries()
                loadMatchEntries()
            }

            is ReportingEvent.OnEndDateSelected -> {
                _state.update { it.copy(filter = it.filter.copy(endDate = event.date, showEndPicker = false)) }
                loadEntries()
                loadMatchEntries()
            }

            ReportingEvent.OnDismissStartPicker ->
                _state.update { it.copy(filter = it.filter.copy(showStartPicker = false)) }

            ReportingEvent.OnDismissEndPicker ->
                _state.update { it.copy(filter = it.filter.copy(showEndPicker = false)) }

            is ReportingEvent.OnSelectReportType -> {
                _state.update { it.copy(reportType = event.reportType) }
                if (event.reportType.usesLoads) ensureLoadReportData()
            }

            is ReportingEvent.OnMissingBoundaryClick -> openFixDialog(event.boundary)

            is ReportingEvent.OnFixMileageChanged ->
                updateFixDialog { it.copy(mileageText = event.text, error = null, warnDifference = null) }

            is ReportingEvent.OnFixLocationChanged ->
                updateFixDialog { it.copy(locationText = event.text) }

            ReportingEvent.OnSaveFix -> saveFix(skipWarning = false)

            ReportingEvent.OnConfirmFixWarning -> saveFix(skipWarning = true)

            ReportingEvent.OnDismissFixDialog -> _state.update { it.copy(fixDialog = null) }

            is ReportingEvent.OnSetTruckClick ->
                _state.update { it.copy(setTruckDialog = SetTruckState(row = event.row)) }

            is ReportingEvent.OnTruckSelected -> setTruck(event.equipmentId)

            ReportingEvent.OnDismissSetTruck -> _state.update {
                if (it.setTruckDialog?.isSaving == true) it else it.copy(setTruckDialog = null)
            }
        }
    }

    private fun openFixDialog(boundary: MissingStartEndBoundary) {
        val fix = buildMissingStartEndFix(boundary, _state.value.entries)
        _state.update {
            it.copy(
                fixDialog = MissingStartEndFixState(
                    boundary = boundary,
                    entriesBefore = fix.entriesBefore,
                    entriesAfter = fix.entriesAfter,
                    endTemplate = fix.endTemplate,
                    startTemplate = fix.startTemplate,
                    mileageText = fix.prefilledMileage?.toString() ?: "",
                    locationText = fix.location,
                )
            )
        }
    }

    private fun updateFixDialog(transform: (MissingStartEndFixState) -> MissingStartEndFixState) {
        _state.update { state -> state.fixDialog?.let { state.copy(fixDialog = transform(it)) } ?: state }
    }

    private fun saveFix(skipWarning: Boolean) {
        val fix = _state.value.fixDialog ?: return
        val location = fix.locationText.trim()
        val mileageValue = fix.mileageText.trim().toLongOrNull() ?: run {
            updateFixDialog { it.copy(error = "Enter a valid odometer reading.") }
            return
        }
        val toSave = listOfNotNull(fix.endTemplate, fix.startTemplate).map {
            it.copy(mileageValue = mileageValue, tripTypeData = TripTypeData.StartEnd(location))
        }
        if (toSave.isEmpty()) return

        viewModelScope.launch {
            updateFixDialog { it.copy(isSaving = true, error = null, warnDifference = null) }
            var largestJump = 0L
            for (entry in toSave) {
                val result = mileageRepository.getOdometerNeighbors(entry.equipmentId, entry.date, entry.mileageValue)
                if (result.error != null) {
                    updateFixDialog {
                        it.copy(
                            isSaving = false,
                            error = odometerLookupErrorMessage(result.error),
                        )
                    }
                    return@launch
                }
                val neighbors = result.data as? OdometerNeighbors ?: OdometerNeighbors()
                when (val placement = evaluateOdometerPlacement(entry.mileageValue, neighbors)) {
                    is OdometerPlacement.Conflict -> {
                        updateFixDialog {
                            it.copy(isSaving = false, error = odometerConflictMessage(entry.mileageValue, placement))
                        }
                        return@launch
                    }
                    is OdometerPlacement.LargeJump ->
                        largestJump = maxOf(largestJump, placement.milesSincePrevious)
                    OdometerPlacement.Fits -> Unit
                }
            }
            if (largestJump > 0 && !skipWarning) {
                updateFixDialog { it.copy(isSaving = false, warnDifference = largestJump) }
                return@launch
            }
            for (entry in toSave) {
                val result = addMileageEntry.invoke(entry, null)
                if (!result.error.isNullOrEmpty()) {
                    updateFixDialog { it.copy(isSaving = false, error = result.error) }
                    return@launch
                }
            }
            _state.update { it.copy(fixDialog = null) }
        }
    }
}
